import { useEffect, useState } from 'react';

import { useQZTray } from '@goshippo/print';

import {
  type ShippoPrintState,
  type ShippoPrintStatus,
} from '../../types/shippoPrintConnection.type';
import {
  connectShippoPrint,
  disconnectShippoPrint,
  getShippoPrintConnectionSnapshot,
  startShippoPrintConnectionManager,
  subscribeShippoPrintConnection,
} from '../../utils/shippoPrintConnection.util';

export type UseShippoPrintReturn = ShippoPrintState;

const getStatus = ({
  connected,
  connecting,
  isEnsuringConnection,
  lastErrorCode,
}: {
  connected: boolean;
  connecting: boolean;
  isEnsuringConnection: boolean;
  lastErrorCode: unknown;
}): ShippoPrintStatus => {
  if (connected) return 'connected';
  if (isEnsuringConnection || connecting) return 'connecting';
  if (lastErrorCode !== null) return 'error';
  return 'disconnected';
};

export const useShippoPrint = (): UseShippoPrintReturn => {
  const qz = useQZTray();
  const [connectionState, setConnectionState] = useState(getShippoPrintConnectionSnapshot);

  useEffect(() => {
    startShippoPrintConnectionManager();
    setConnectionState(getShippoPrintConnectionSnapshot());
    return subscribeShippoPrintConnection(() =>
      setConnectionState(getShippoPrintConnectionSnapshot()),
    );
  }, []);

  return {
    activeIntent: connectionState.activeIntent,
    connect: connectShippoPrint,
    connected: qz.connected,
    connecting: qz.connecting,
    disconnect: disconnectShippoPrint,
    isEnsuringConnection: connectionState.isEnsuringConnection,
    lastCloseInfo: qz.lastCloseInfo,
    lastError: qz.lastError,
    lastErrorCode: qz.lastErrorCode,
    status: getStatus({
      connected: qz.connected,
      connecting: qz.connecting,
      isEnsuringConnection: connectionState.isEnsuringConnection,
      lastErrorCode: qz.lastErrorCode,
    }),
  };
};
